import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Alert, Box, Collapse, Grid, IconButton, Typography } from '@mui/material';
import { get } from 'lodash';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMixpanel } from 'react-mixpanel-browser';

import { usePostEmail, useUserAccount } from '@/api/userAccount';
import { FormInputText } from '@/components/form/FormInputText';
import { ResetUpdateButtonGroup } from '@/components/userAccount/ResetUpdateButtonGroup';
import { useDisclosure } from '@/hooks/useDisclosure';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { borderGrey } from '@/theme';

export const EmailSection = () => {
    const { toggle: toggleEditMode, isOpen: isEditMode, close: disableEditMode } = useDisclosure();
    const { data: accountData } = useUserAccount();
    const mixpanel = useMixpanel();
    const { mutateAsync: postEmail, isError, error, reset: postEmailMutationReset } = usePostEmail();
    const {
        control: emailControl,
        reset: emailReset,
        handleSubmit: emailHandleSubmit,
        formState: emailFormState,
    } = useForm<{ email: string }>({
        defaultValues: { email: '' },
        mode: 'onChange',
    });
    useUnsavedChangesGuard(emailFormState.isDirty, 'email-section');

    const onEmailSubmit = async (formData: { email: string }) => {
        try {
            await postEmail({ newEmail: formData.email });
            mixpanel?.track('User Account - Email Updated');
            disableEditMode();
        } catch (err) {
            mixpanel?.track('User Account - Email Update Failed');
            console.error(err);
        }
    };
    const onEditClick = () => {
        toggleEditMode();
        onEmailReset();
        postEmailMutationReset();
    };
    const onEmailReset = () => {
        emailReset({ email: get(accountData, 'data.user.email', '') });
        postEmailMutationReset();
    };

    useEffect(() => {
        emailReset({ email: get(accountData, 'data.user.email', '') });
    }, [accountData, emailReset]);

    return (
        <Box data-testid="user-account-email-section">
            <Typography color="text.secondary" variant="h5" sx={{ mb: 2 }}>
                Email
                <IconButton
                    size="small"
                    sx={{ display: 'relative', bottom: 5 }}
                    onClick={onEditClick}
                    data-testid="email-section-edit-mode-button"
                >
                    {isEditMode ? <ExpandLessIcon /> : <EditIcon />}
                </IconButton>
            </Typography>
            <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 8 }}>
                    <FormInputText
                        name="email"
                        control={emailControl}
                        label="Email"
                        inputFieldProps={{
                            id: 'email',
                            type: 'text',
                            size: 'medium',
                            disabled: !isEditMode,
                            sx: {
                                '&& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: isEditMode ? 'info.main' : borderGrey,
                                },
                                '&& .MuiFormLabel-root:not(.Mui-disabled)': {
                                    color: 'info.main',
                                },
                                '&& .MuiOutlinedInput-input.Mui-disabled': {
                                    WebkitTextFillColor: 'black',
                                },
                            },
                        }}
                        controllerProps={{
                            rules: {
                                required: 'Field is required',
                                pattern: {
                                    value: /\S+@\S+\.\S+/,
                                    message: 'Entered value does not match email format',
                                },
                            },
                        }}
                    />
                    <Collapse in={isError}>
                        <Alert sx={{ my: 1 }} severity="error">
                            {error?.message ?? ''}
                        </Alert>
                    </Collapse>
                </Grid>
            </Grid>
            <Collapse in={isEditMode}>
                <ResetUpdateButtonGroup
                    onReset={onEmailReset}
                    onSubmit={emailHandleSubmit(onEmailSubmit)}
                    isResetDisabled={!emailFormState.isDirty || emailFormState.isSubmitting}
                    isUpdateDisabled={!emailFormState.isDirty || emailFormState.isSubmitting}
                    isSubmitting={emailFormState.isSubmitting}
                />
            </Collapse>
        </Box>
    );
};
